import React, { useState, useEffect, useRef } from 'react';
import { Edit2, Save, X, Filter, ChevronDown, ChevronUp, MapPin, Globe, Search } from 'lucide-react';
import apiService from '../config';
import { useLanguage } from '../StoreContext/LanguageContext';

const HighwayMarketValueBail = ({ onBack, setSelectedComponent }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [editRowIndex, setEditRowIndex] = useState(null);
  const [editFormData, setEditFormData] = useState({
    irrigable_area: '',
    group_valuation: ''
  });
  const [loading, setLoading] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [projectName, setProjectName] = useState(null);
  const [proposalId, setProposalId] = useState(null);
  const [proposalData, setProposalData] = useState(null);
  const [usersLAO, setusersLAO] = useState(null);
  const { language, translations } = useLanguage();

  // Location states
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [villages, setVillages] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedTaluka, setSelectedTaluka] = useState('');
  const [selectedVillage, setSelectedVillage] = useState('');

  // Filter states
  const [filterMode, setFilterMode] = useState('all');
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Loading states
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingTalukas, setLoadingTalukas] = useState(false);
  const [loadingVillages, setLoadingVillages] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);

  const ROLES = {
    COLLECTOR: 1,
    LAO: 3,
    SAMANVAY: 6,
    PROJECT_INCHARGE: 18,
    DATA_ENTRY: 14
  };

  const userData = JSON.parse(localStorage.getItem('userData'));
  const userRole = parseInt(userData?.data?.user_role || userData?.user_role, 10);

  const tableRef = useRef(null);

  // Helper functions
  const hasValidLocationSelection = selectedDistrict;
  const canShowData = filterMode === 'all' || (filterMode === 'location' && hasValidLocationSelection);

  const getCurrentLocationDescription = () => {
    if (filterMode === 'all') {
      return 'All Locations';
    }

    if (selectedDistrict && selectedTaluka && selectedVillage) {
      return `Village: ${selectedVillage}, ${selectedTaluka}, ${selectedDistrict}`;
    } else if (selectedDistrict && selectedTaluka) {
      return `All Villages in ${selectedTaluka}, ${selectedDistrict}`;
    } else if (selectedDistrict) {
      return `All Records in ${selectedDistrict} District`;
    } else {
      return 'No location selected';
    }
  };

  // Load districts
  const loadDistricts = async () => {
    setLoadingDistricts(true);
    try {
      const response = await apiService.getDistricts();
      const districtsData = Array.isArray(response.data) ? response.data : [];
      setDistricts(districtsData);

      // Auto-select from selectedProposal
      const proposalStr = localStorage.getItem('selectedProposal');
      if (proposalStr) {
        try {
          const proposal = JSON.parse(proposalStr);
          const desired = proposal.dist_name || proposal.distName;
          if (desired && !selectedDistrict) {
            const found = districtsData.find(d =>
              d.dist_name === desired || d.name === desired || d.eng_dist_name === desired
            );
            setSelectedDistrict(found ? (found.dist_name || found.id) : desired);
          }
        } catch (e) {
          console.error('Error parsing selectedProposal in loadDistricts:', e);
        }
      }
    } catch (error) {
      console.error("Failed to fetch districts:", error);
      setDistricts([]);
    } finally {
      setLoadingDistricts(false);
    }
  };

  // Load talukas
  const loadTalukas = async () => {
    if (!selectedDistrict) return;

    setLoadingTalukas(true);
    try {
      const response = await apiService.getTalukas(selectedDistrict);
      const talukasData = Array.isArray(response.data) ? response.data : [];
      setTalukas(talukasData);

      // Auto-select from selectedProposal
      const proposalStr = localStorage.getItem('selectedProposal');
      if (proposalStr) {
        try {
          const proposal = JSON.parse(proposalStr);
          const desiredTaluka = proposal.taluka_name || proposal.talukaName;
          if (desiredTaluka && !selectedTaluka) {
            const found = talukasData.find(t =>
              t.taluka_name === desiredTaluka || t.tehsil_name === desiredTaluka || t.eng_tehsil_name === desiredTaluka
            );
            setSelectedTaluka(found ? (found.taluka_name || found.id) : desiredTaluka);
          }
        } catch (e) {
          console.error('Error parsing selectedProposal in loadTalukas:', e);
        }
      } else {
        setSelectedTaluka('');
        setVillages([]);
        setSelectedVillage('');
      }
    } catch (error) {
      console.error("Failed to fetch talukas:", error);
      setTalukas([]);
    } finally {
      setLoadingTalukas(false);
    }
  };

  // Load villages
  const loadVillages = async () => {
    if (!selectedDistrict || !selectedTaluka) return;

    setLoadingVillages(true);
    try {
      const response = await apiService.getVillages(selectedDistrict, selectedTaluka);
      const villagesData = Array.isArray(response.data) ? response.data : [];
      setVillages(villagesData);

      // Auto-select from selectedProposal
      const proposalStr = localStorage.getItem('selectedProposal');
      if (proposalStr) {
        try {
          const proposal = JSON.parse(proposalStr);
          const desiredVillage = proposal.village_name || proposal.villageName;
          if (desiredVillage && !selectedVillage) {
            const found = villagesData.find(v =>
              v.village_name === desiredVillage ||
              v.Village_Name === desiredVillage ||
              v.eng_village_name === desiredVillage ||
              v.name === desiredVillage
            );
            setSelectedVillage(found ? (found.village_name || found.id || found.village_id) : desiredVillage);
          }
        } catch (e) {
          console.error('Error parsing selectedProposal in loadVillages:', e);
        }
      } else {
        setSelectedVillage('');
      }
    } catch (error) {
      console.error("Failed to fetch villages:", error);
      setVillages([]);
    } finally {
      setLoadingVillages(false);
    }
  };

  // Handle location changes
  const handleDistrictChange = (e) => {
    const selectedDistrictId = e.target.value;
    setSelectedDistrict(selectedDistrictId);
    setSelectedTaluka('');
    setSelectedVillage('');
    setTalukas([]);
    setVillages([]);
    setData([]);
    setFilteredData([]);
  };

  const handleTalukaChange = (e) => {
    const selectedTalukaId = e.target.value;
    setSelectedTaluka(selectedTalukaId);
    setSelectedVillage('');
    setVillages([]);
    setData([]);
    setFilteredData([]);
  };

  const handleVillageChange = (e) => {
    const selectedVillageId = e.target.value;
    setSelectedVillage(selectedVillageId);
    setData([]);
    setFilteredData([]);
  };

  // Handle search functionality
  const handleSearch = (searchTerm) => {
    if (!searchTerm) {
      setFilteredData(data);
      return;
    }

    const filtered = data.filter(record =>
      record.survey_no?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.irrigable_area?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.group_valuation?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.hectareAakarni?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.aakarGat?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.hektareDar?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
  };

  // Fetch projectId and proposalId
  useEffect(() => {
    const getProjectInfo = () => {
      const currentProjectData = localStorage.getItem('currentProjectData');
      if (currentProjectData) {
        try {
          const projectData = JSON.parse(currentProjectData);
          return {
            id: projectData.project_id || projectData.id,
            name: projectData.project_name || projectData.name
          };
        } catch (e) {
          console.error('Error parsing current project data (currentProjectData):', e);
        }
      }

      const selectedProject = localStorage.getItem('selectedProject');
      if (selectedProject) {
        try {
          const project = JSON.parse(selectedProject);
          return {
            id: project.project_id || project.id,
            name: project.project_name || project.name
          };
        } catch (e) {
          console.error('Error parsing selected project (selectedProject):', e);
        }
      }

      const userData = localStorage.getItem('userData');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          const projectId = user.data?.project_id || user.project_id;
          const projectName = user.data?.project_name || user.project_name || 'Unknown Project';
          return {
            id: projectId,
            name: projectName
          };
        } catch (e) {
          console.error('Error parsing user data (userData):', e);
        }
      }
      return { id: null, name: null };
    };

    const projectInfo = getProjectInfo();
    setProjectId(projectInfo.id);
    setProjectName(projectInfo.name);
  }, []);

  useEffect(() => {
    const getProposalInfo = () => {
      const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');
      if (currentProposal) {
        try {
          return {
            proposalId: currentProposal?.data?.default_proposal ||
              currentProposal?.data?.proposal_id ||
              currentProposal?.default_proposal ||
              currentProposal?.proposal_id ||
              null
          };
        } catch (e) {
          console.error('Error parsing current project data (getProposalInfo):', e);
        }
      }
      return { proposalId: null };
    };

    const proposalInfo = getProposalInfo();
    setProposalId(proposalInfo.proposalId);
  }, []);

  // Auto-select from selectedProposal
  useEffect(() => {
    try {
      const selProposalStr = localStorage.getItem('selectedProposal');
      if (selProposalStr) {
        const proposal = JSON.parse(selProposalStr);
        const dist = proposal.dist_name || proposal.distName || '';
        const tal = proposal.taluka_name || proposal.talukaName || '';
        const vill = proposal.village_name || proposal.villageName || '';

        if (dist) setSelectedDistrict(dist);
        if (tal) setSelectedTaluka(tal);
        if (vill) setSelectedVillage(vill);

        if (dist || tal || vill) {
          setShowFilterPanel(true);
          return;
        }
      }

      const saved = localStorage.getItem('selectedLocationInfo');
      if (saved) {
        const loc = JSON.parse(saved);
        if (loc.district) setSelectedDistrict(loc.district);
        if (loc.taluka) setSelectedTaluka(loc.taluka);
        if (loc.village) setSelectedVillage(loc.village);
      }
    } catch (err) {
      console.error('Error reading selectedProposal for defaults', err);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProposalData();
    fetchusersLAO();
  }, [projectId, proposalId]);

  // Fetch Proposal Data
  const fetchProposalData = async () => {

    try {
      const response = await apiService.getProposalPublish(projectId, proposalId);
      if (response.status === 200 && response.data) {
        setProposalData(response.data);
      }
    } catch (err) {
      console.error('Error fetching proposal data:', err);
    }
  };

  // fetch LAO users
  const fetchusersLAO = async () => {
    const role = 3;
    try {
      const response = await apiService.getAssignLAO(role, projectId, proposalId);
      console.log('LAO users:', response);
      if (response.status === 200 && response.data) {
        setusersLAO(Array.isArray(response.data) ? response.data : []);
      } else {
        setusersLAO([]);
      }
    } catch (error) {
      console.error('Error fetching LAO users:', error);
      setusersLAO([]);
    }
  };

  // Load market value data for all locations
  const loadMarketValueDataForAll = async () => {
    if (!projectId || !proposalId) return;

    setLoadingRecords(true);
    try {
      const response = await apiService.getOwnerMarketValue(projectId, proposalId);
      let marketValues = [];

      if (Array.isArray(response.data)) {
        marketValues = response.data;
      } else if (Array.isArray(response.data?.data)) {
        marketValues = response.data.data;
      } else if (Array.isArray(response)) {
        marketValues = response;
      }

      const formattedData = marketValues.map(item => ({
        survey_no: item.survey_no || '',
        irrigable_area: item.irrigable_area || '',
        group_valuation: item.group_valuation || '',
        hectareAakarni: item.rate_per_hectare || '',
        aakarGat: item.market_group_no || '',
        market_amount: item.market_amount || item.hektareDar || '',
        hektareDar: item.market_amount || item.hektareDar || ''

      }));

      setData(formattedData);
      setFilteredData(formattedData);
    } catch (error) {
      console.error('Error fetching all market value data:', error);
      setData([]);
      setFilteredData([]);
    } finally {
      setLoadingRecords(false);
    }
  };

  // Load market value data for specific location
  const loadMarketValueDataForLocation = async () => {
    if (!projectId || !proposalId) return;

    setLoadingRecords(true);
    try {
      let url = `getOwnerMarketValue?projectId=${projectId}&proposalId=${proposalId}`;

      if (selectedDistrict) {
        url += `&distName=${encodeURIComponent(selectedDistrict)}`;
      }
      if (selectedTaluka) {
        url += `&talukaName=${encodeURIComponent(selectedTaluka)}`;
      }
      if (selectedVillage) {
        url += `&villageName=${encodeURIComponent(selectedVillage)}`;
      }

      const response = await apiService.get(url);
      let marketValues = [];

      if (Array.isArray(response.data)) {
        marketValues = response.data;
      } else if (Array.isArray(response.data?.data)) {
        marketValues = response.data.data;
      } else if (Array.isArray(response)) {
        marketValues = response;
      }

      const formattedData = marketValues.map(item => ({
        survey_no: item.survey_no || '',
        irrigable_area: item.irrigable_area || '',
        group_valuation: item.group_valuation || '',
        hectareAakarni: item.rate_per_hectare || '',
        aakarGat: item.market_group_no || '',
        market_amount: item.market_amount || item.hektareDar || '',
        hektareDar: item.market_amount || item.hektareDar || ''
      }));

      setData(formattedData);
      setFilteredData(formattedData);
    } catch (error) {
      console.error('Error fetching market value data for location:', error);
      setData([]);
      setFilteredData([]);
    } finally {
      setLoadingRecords(false);
    }
  };

  // Load initial data
  useEffect(() => {
    loadDistricts();
  }, []);

  // Load talukas when district changes
  useEffect(() => {
    loadTalukas();
  }, [selectedDistrict]);

  // Load villages when taluka changes
  useEffect(() => {
    loadVillages();
  }, [selectedDistrict, selectedTaluka]);

  // Load data based on filter mode
  useEffect(() => {
    if (filterMode === 'all' && projectId && proposalId) {
      loadMarketValueDataForAll();
    }
  }, [filterMode, projectId, proposalId]);

  useEffect(() => {
    if (filterMode === 'location' && selectedDistrict && projectId && proposalId) {
      loadMarketValueDataForLocation();
    } else if (filterMode === 'location') {
      setData([]);
      setFilteredData([]);
    }
  }, [filterMode, selectedDistrict, selectedTaluka, selectedVillage, projectId, proposalId]);

  // Handle edit button click
  const handleEditClick = (index) => {
    setEditRowIndex(index);
    setEditFormData({
      irrigable_area: filteredData[index].irrigable_area,
      group_valuation: filteredData[index].group_valuation,
      market_amount: filteredData[index].market_amount || filteredData[index].hektareDar
    });
  };

  // Handle input change in edit form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle save for edited row
  const handleSave = async (index) => {
    if (!projectId || !proposalId) {
      alert('No project or proposal ID found for update.');
      return;
    }

    try {
      setLoading(true);
      const updateData = {
        survey_no: filteredData[index].survey_no,
        irrigable_area: editFormData.irrigable_area,
        group_valuation: editFormData.group_valuation,
        market_amount: editFormData.market_amount,
        project_id: projectId,
        proposal_id: proposalId,
      };

      const result = await apiService.postAddUpdateOwnerMarketValue(projectId, proposalId, updateData);

      if (result.status === 200) {
        alert('✅ Market Value Updated Successfully');

        // Update both data arrays
        const updatedItem = { ...filteredData[index], ...editFormData };

        setFilteredData(prev => {
          const newData = [...prev];
          newData[index] = updatedItem;
          return newData;
        });

        // Also update main data array
        setData(prev => {
          const newData = [...prev];
          const mainIndex = newData.findIndex(item => item.survey_no === filteredData[index].survey_no);
          if (mainIndex !== -1) {
            newData[mainIndex] = updatedItem;
          }
          return newData;
        });

        setEditRowIndex(null);

        // 🔄 Force API reload after update
        if (filterMode === 'location' && selectedDistrict) {
          await loadMarketValueDataForLocation();   // reload with district/taluka/village
        } else {
          await loadMarketValueDataForAll();        // reload all
        }

      } else {
        alert('Failed to update market value.');
      }
    } catch (error) {
      console.error('Error updating market value:', error);
      alert('❌ Error while updating market value.');
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel edit
  const handleCancel = () => {
    setEditRowIndex(null);
    setEditFormData({ irrigable_area: '', group_valuation: '' });
  };

  // Handle print functionality
  const handlePrint = () => {
    if (!tableRef.current) {
      alert("No content available to print.");
      return;
    }

    const printableTable = `
      <table style="margin-top:20px; width: 100%; border-collapse: collapse; border: 1px solid #666;">
        <thead>
          <tr>
            <th style="border: 1px solid #666; padding: 8px; text-align: center;">${translations[language]?.MarketGatNumber || 'गट क्रमांक'}</th>
            <th style="border: 1px solid #666; padding: 8px; text-align: center;">${translations[language]?.EligibleArea || 'पात्र क्षेत्र'}</th>
            <th style="border: 1px solid #666; padding: 8px; text-align: center;">${translations[language]?.DeductionReason || 'कपात कारण'}</th>
            <th style="border: 1px solid #666; padding: 8px; text-align: center;">${translations[language]?.HectareDeduction || 'हेक्टर कपात'}</th>
            <th style="border: 1px solid #666; padding: 8px; text-align: center;">${translations[language]?.AreaGat || 'क्षेत्र गट'}</th>
            <th style="border: 1px solid #666; padding: 8px; text-align: center;">${translations[language]?.HectareRate || 'हेक्टर दर'}</th>
          </tr>
        </thead>
        <tbody>
          ${filteredData.map((row, index) => `
            <tr style="background-color: ${index % 2 === 0 ? '#ffffff' : '#f3f4f6'};">
              <td style="border: 1px solid #666; padding: 8px; text-align: center;">${row.survey_no}</td>
              <td style="border: 1px solid #666; padding: 8px; text-align: center;">${row.irrigable_area}</td>
              <td style="border: 1px solid #666; padding: 8px; text-align: center;">${row.group_valuation}</td>
              <td style="border: 1px solid #666; padding: 8px; text-align: center;">${row.hectareAakarni}</td>
              <td style="border: 1px solid #666; padding: 8px; text-align: center;">${row.aakarGat}</td>
              <td style="border: 1px solid #666; padding: 8px; text-align: center;">${row.market_amount}</td>

            </tr>
          `).join('')}
        </tbody>
      </table>
    `;

    const win = window.open('', '_blank');
    win.document.write(`
      <html>
        <head>
          <style>
            @page {
          size: A4 portrait;   /* Orientation */
          margin-top: 0.19in;
          margin-bottom: 0.25in;
          margin-left: 0.81in;
          margin-right: 0.5in;
        }
        body {
          font-family: 'DVOT-Surekh', 'Mangal', 'Arial', sans-serif;
        }
            table {
              width: 100%;
              border-collapse: collapse;
              border: 1px solid #666;
            }
            th, td {
              border: 1px solid #666;
              padding: 8px;
              text-align: center;
            }
            th {
              font-weight: bold;
            }
            .title {
              text-align: center;
              font-size: 18px;
              margin-bottom: 6px;
            }
            .signature-block {
              text-align: right;
              padding-top: 2rem; /* 
            }
            @media print {
              body { margin: 10mm; }
            }
          </style>
        </head>
        <body>
          <div class="title">प्रकल्प नाव: ${projectName} | प्रस्ताव नाव/क्रमांक: ${proposalData.section_11_bhusampadan_no || ''} </div>
          <div class="title">${translations[language]?.MarketPriceChart || 'बाजार मूल्य तक्ता'}</div>
          ${printableTable}

          <div class="signature-block">
            <p>${usersLAO?.[0]?.full_name || ''}</p>
            <p>${usersLAO?.[0]?.designation || ''}</p>
          </div>
        </body>
      </html>
    `);

    win.document.close();
    win.print();
  };

  // Loader display
  if (loadingRecords) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="flex justify-center items-center h-80">
          <div className="text-center">
            <div className="inline-flex relative">
              <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-transparent animate-spin"></div>
              <div className="w-16 h-16 rounded-full border-4 border-purple-500 border-b-transparent border-r-transparent animate-spin absolute inset-0" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <MapPin className="h-6 w-6 text-purple-400 animate-pulse" />
              </div>
            </div>
            <p className="text-gray-400 mt-4">{translations[language]?.LoadingMarketPrices}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">

        {/* Search and Actions Bar - Always show when not loading */}
        {!loadingRecords && canShowData && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">

              {/* Action Buttons */}
              <div className="flex gap-3">
                {/* Filter Button */}
                <button
                  onClick={() => setShowFilterPanel(!showFilterPanel)}
                  className="flex items-center space-x-2 px-4 py-2.5 bg-orange-600 text-white rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md hover:bg-orange-700"
                >
                  <Filter className="h-4 w-4" />
                  <span>{translations[language]?.Filters || 'Filters'}</span>
                  {showFilterPanel ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>

                {/* Print Button */}
                <button
                  onClick={handlePrint}
                  disabled={filteredData.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {translations[language]?.Print || 'Print'}
                </button>
              </div>
              {/* <button
              onClick={() => setSelectedComponent("DirectPayment")}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
             {translations[language].DirectPurchase}
            </button> */}
            </div>
          </div>
        )}

        {/* Filter Panel - Appears below the action bar */}
        {showFilterPanel && !loadingRecords && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-gray-200">
            <div className="space-y-6">

              {/* Filter Mode Selection */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1 bg-gray-100 rounded-xl p-1.5 shadow-inner">
                  <button
                    onClick={() => setFilterMode('location')}
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${filterMode === 'location'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                  >
                    <MapPin className="h-4 w-4" />
                    <span>{translations[language]?.FilterByLocation || 'Filter by Location'}</span>
                  </button>
                  <button
                    onClick={() => setFilterMode('all')}
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${filterMode === 'all'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                  >
                    <Globe className="h-4 w-4" />
                    <span>{translations[language]?.AllRecords || 'All Records'}</span>
                  </button>
                </div>
              </div>

              {/* Location Selection - Only show when location mode is selected */}
              {filterMode === 'location' && (
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <div className="space-y-4">

                    {/* Header */}
                    <div className="flex items-center">
                      <MapPin className="h-5 w-5 text-blue-600 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-800">{translations[language]?.SelectLocation || 'Select Location'}</h3>
                    </div>

                    {/* Location Dropdowns */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                      {/* District Dropdown */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {translations[language]?.district || 'District'} <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={selectedDistrict}
                          onChange={handleDistrictChange}
                          disabled={loadingDistricts}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                        >
                          <option value="">
                            {loadingDistricts ? 'Loading Districts...' : 'Select District'}
                          </option>
                          {districts.map((district) => (
                            <option key={district.dist_name} value={district.dist_name}>
                              {district.dist_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Taluka Dropdown */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {translations[language]?.taluka || 'Taluka'} <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={selectedTaluka}
                          onChange={handleTalukaChange}
                          disabled={!selectedDistrict || loadingTalukas}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                        >
                          <option value="">
                            {loadingTalukas ? 'Loading Talukas...' : 'Select Taluka'}
                          </option>
                          {talukas.map((taluka) => (
                            <option key={taluka.taluka_name} value={taluka.taluka_name}>
                              {taluka.taluka_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Village Dropdown */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {translations[language]?.village || 'Village'} <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={selectedVillage}
                          onChange={handleVillageChange}
                          disabled={!selectedTaluka || loadingVillages}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                        >
                          <option value="">
                            {loadingVillages ? 'Loading Villages...' : 'Select Village'}
                          </option>
                          {villages.map((village) => {
                            const villageId = village.village_name;
                            const villageName = village.village_name;
                            return (
                              <option key={villageId} value={villageId}>
                                {villageName}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>

                    {/* Status Messages */}
                    {filterMode === 'location' && !hasValidLocationSelection && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <p className="text-sm text-amber-700">
                          {translations[language]?.PleaseSelectAll || 'Please select at least district to view data.'}
                        </p>
                      </div>
                    )}

                    {filterMode === 'location' && hasValidLocationSelection && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm text-green-700 flex items-center">
                          <span className="mr-2">✓</span>
                          {translations[language]?.ShowingDataFor || 'Showing data for'} {getCurrentLocationDescription()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Data Table - Show when records exist */}
        {canShowData && filteredData.length > 0 && !loadingRecords && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="text-center p-4">
              <h2 className="text-xl font-bold">{translations[language]?.MarketPriceChart}</h2>
            </div>

            <div ref={tableRef} className="overflow-x-auto">
              <table className="w-full border border-gray-400 text-center">
                <thead>
                  <tr className="bg-cyan-700 text-white">
                    {/* {userRole === ROLES.LAO && ( */}
                    {/* Data Entry */}
                    {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
                      <th className="border border-gray-400 px-2 py-1">{translations[language]?.MarketAction}</th>
                    )}
                    <th className="border border-gray-400 px-2 py-1">{translations[language]?.MarketGatNumber}</th>
                    <th className="border border-gray-400 px-2 py-1">{translations[language]?.EligibleArea}</th>
                    <th className="border border-gray-400 px-2 py-1">{translations[language]?.DeductionReason}</th>
                    <th className="border border-gray-400 px-2 py-1">{translations[language]?.HectareDeduction}</th>
                    <th className="border border-gray-400 px-2 py-1">{translations[language]?.AreaGat}</th>
                    <th className="border border-gray-400 px-2 py-1">{translations[language]?.HectareRate}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-100"}>
                      {/* {userRole === ROLES.LAO && ( */}
                      {/* Data Entry */}
                      {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
                        <td className="border border-gray-400 px-2 py-1">
                          {editRowIndex === index ? (
                            <div className="flex justify-center gap-2">
                              <button
                                onClick={() => handleSave(index)}
                                disabled={loading}
                                className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                              >
                                {loading ? (
                                  <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>{translations[language]?.Saving || 'Saving...'}</span>
                                  </>
                                ) : (
                                  <>
                                    <Save size={16} />
                                  </>
                                )}
                              </button>

                              <button
                                onClick={handleCancel}
                                className="flex items-center gap-1 px-2 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleEditClick(index)}
                              className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                              <Edit2 size={16} />
                            </button>
                          )}
                        </td>
                      )}
                      <td className="border border-gray-400 px-2 py-1">{row.survey_no}</td>
                      <td className="border border-gray-400 px-2 py-1">
                        {editRowIndex === index ? (
                          <input
                            type="text"
                            name="irrigable_area"
                            value={editFormData.irrigable_area}
                            onChange={handleInputChange}
                            className="w-full px-2 py-1 border border-gray-300 rounded-md"
                          />
                        ) : (
                          row.irrigable_area
                        )}
                      </td>
                      <td className="border border-gray-400 px-2 py-1">
                        {editRowIndex === index ? (
                          <input
                            type="text"
                            name="group_valuation"
                            value={editFormData.group_valuation}
                            onChange={handleInputChange}
                            className="w-full px-2 py-1 border border-gray-300 rounded-md"
                          />
                        ) : (
                          row.group_valuation
                        )}
                      </td>
                      <td className="border border-gray-400 px-2 py-1">{row.hectareAakarni}</td>
                      <td className="border border-gray-400 px-2 py-1">{row.aakarGat}</td>
                      <td className="border border-gray-400 px-2 py-1">
                        {editRowIndex === index ? (
                          <input
                            type="text"
                            name="market_amount"
                            value={editFormData.market_amount}
                            onChange={handleInputChange}
                            className="w-full px-2 py-1 border border-gray-300 rounded-md"
                          />
                        ) : (
                          row.market_amount || row.hektareDar || ''
                        )}
                      </td>
                      {/* <td className="border border-gray-400 px-2 py-1">{row.hektareDar}</td> */}

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Data Message */}
        {canShowData && filteredData.length === 0 && !loadingRecords && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="text-gray-400 mb-4">
              <MapPin className="h-16 w-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{translations[language]?.NoRecordsFound || 'No Records Found'}</h3>
            <p className="text-gray-600 mb-2">
              {translations[language]?.NoMarketValueData || 'No market value data found for the selected criteria.'}
            </p>
            <p className="text-gray-500 text-sm mt-2">
              {translations[language]?.TryAnother || 'Try selecting different location or use "All Records" filter.'}
            </p>
          </div>
        )}

        {/* Location Selection Required Message */}
        {filterMode === 'location' && !hasValidLocationSelection && !loadingRecords && (
          <div className="mt-6 text-center py-12">
            <div className="max-w-md mx-auto">
              <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium mb-2">{translations[language]?.LocationSelectionRequired || 'Location Selection Required'}</p>
              <p className="text-gray-400 text-sm">{translations[language]?.PleaseSelectFromFilter || 'Please select at least district from the filter panel above to view data.'}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HighwayMarketValueBail;